<template>
    <div ref="countryModalRef" class="relative w-full">
        <slot name="items" v-bind="itemsScopeProps">
            <div
                class="th-input-lg th-input flex items-center justify-between gap-1 p-1"
                :class="{'border-eureka-500': mannedCountryPopover}"
                @click="() => toggle()"
            >
                <div v-if="chosenList.length" class="flex h-9 grow items-center gap-1 overflow-hidden">
                    <template
                        v-for="(item, index) in chosenList"
                        :key="index"
                    >
                        <slot name="item" v-bind="itemsScopeProps" :item="item" :index="index">
                            <span class="flex h-6 shrink-0 items-center gap-1 rounded bg-grey-20 pl-2 pr-1 text-xs text-black">
                                <span :class="`authority-position authority-position--${item.id}`"></span>
                                <div class="hover:bg-gray-450 group h-4 w-4 hover:cursor-pointer hover:rounded-full">
                                    <IconCloseSmall class="group-hover:fill-white" @click="removeItem(item)" />
                                </div>
                            </span>
                        </slot>
                    </template>
                </div>
                <div v-else class="text-xs text-yellow-500">{{ t('leastOne') }}</div>
                <span class="text-sm">
                    <span class="text-black">{{ chosenList.length }}</span>
                    <span class="text-grey-60">/{{ total }}</span>
                </span>
            </div>
        </slot>
        <div
            ref="countrySelectRef"
            :style="floatingStyles"
            class="th-menu z-10 max-h-[550px] min-w-[300px] max-w-[420px] overflow-y-auto p-1" v-show="mannedCountryPopover"
        >

            <div class="relative">
                <PtIcon icon="SolidSearchLeft" class="absolute left-1 text-xl top-1/2 -translate-y-1/2 fill-gray-90"/>
                <input class="th-input w-full pl-7" :placeholder="t('filterCountry')" v-model="filterKeyword">
            </div>

            <div v-if="haveNoFilterData" class="test-sm p-5 text-grey-55">
                {{t('filterNoData')}}
            </div>

            <div v-else>

                <div class="flex cursor-pointer items-center py-1.5"
                     @click="handleCheckAllCountry">
                    <input type="checkbox" :checked="isAllCountryChecked" :disabled="!!filterKeyword"
                           class="th-checkbox pointer-events-none mx-1" :indeterminate="isIndeterminateCountry" />
                    <span class="ml-1 text-sm">{{t('allCountry')}}</span>
                </div>

                <div class="pl-6">
                    <!-- ip5 -->
                    <CheckboxGroup
                        v-if="afterFilterListData?.ip5.length"
                        :allLabel="t('ip5')"
                        :disabledAllCheck="!!filterKeyword"
                        :list-data="afterFilterListData?.ip5" v-model="checkedCountry.ip5">
                        <template #icon-before-text="{ data }">
                            <span class="shrink-0" :class="`authority-position authority-position--${data.id}`"></span>
                        </template>
                        <template #tooltip="{ data }">
                            <slot name="tooltip" :data="data" />
                        </template>
                    </CheckboxGroup>

                    <!-- major -->
                    <CheckboxGroup
                        v-if="afterFilterListData?.major.length"
                        :allLabel="t('major')"
                        :disabledAllCheck="!!filterKeyword"
                        :list-data="afterFilterListData?.major" v-model="checkedCountry.major">
                        <template #icon-before-text="{ data }">
                            <span class="shrink-0" :class="`authority-position authority-position--${data.id}`"></span>
                        </template>
                        <template #tooltip="{ data }">
                            <slot name="tooltip" :data="data" />
                        </template>
                    </CheckboxGroup>

                    <!-- others -->
                    <CheckboxGroup
                        v-if="afterFilterListData?.others.length"
                        :allLabel="t('others')"
                        :disabledAllCheck="!!filterKeyword"
                        :list-data="afterFilterListData?.others" v-model="checkedCountry.others">
                        <template #icon-before-text="{ data }">
                            <span class="shrink-0" :class="`authority-position authority-position--${data.id}`"></span>
                        </template>
                        <template #tooltip="{ data }">
                            <slot name="tooltip" :data="data" />
                        </template>
                    </CheckboxGroup>
                </div>

            </div>

        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, reactive, watchEffect, watch } from 'vue';
import { PtIcon } from 'patsnap-biz';
import { onClickOutside } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import _ from 'lodash';
import IconCloseSmall from './IconCloseSmall.vue';
import type { ISelectData, PatentCountries } from './type';
import { CheckboxGroup } from '@eureka/shared/checkbox-group';
import { dbConfigDeferred } from './use-country-select';
import { messages } from './locales';
import { useFloating, flip, offset, type UseFloatingOptions, autoUpdate } from '@floating-ui/vue';
import '@patsnap-ui/icon/assets/solid/SearchLeft.svg'

const props = withDefaults(defineProps<{
    floatingOptions: UseFloatingOptions,
    countryEnabled?: (key: string, country: string) => boolean,
}>(), {
    floatingOptions: () => ({}),
})

const { t } = useI18n(
    {
        messages,
    }
);

const allCountries = reactive<PatentCountries>({
    ip5: [],
    major: [],
    others: [],
})
const afterFilterListData = ref<PatentCountries>({
    ip5: [],
    major: [],
    others: [],
})

const filterKeyword = ref('')
const countrySelectRef = ref(null)

const total = computed(() => {
    return _.flatMap(_.values(allCountries)).length
})

const mannedCountryPopover = ref(false)

const toggle = (forceValue?: boolean) => {
    mannedCountryPopover.value = forceValue ?? !mannedCountryPopover.value
}

const checkedCountry = ref({
    ip5: [],
    major: [],
    others: [],
})
const countryModalRef = ref(null)

const checkedCountryIdList = computed(() => {
    return _.flatMap(_.values(checkedCountry.value)) as string[]
})

const chosenList = computed(() => {
    const allItems = _.flatMap(_.values(allCountries), (arr:ISelectData[]) => {
        return arr.filter((data) => checkedCountryIdList.value.includes(data.id as string))
    });
    return allItems
})

const isAllCountryChecked = computed(() => {
    // Count only enabled (non-disabled) countries for comparison
    const enabledCountriesCount = _.flatMap(_.values(allCountries), (arr:ISelectData[]) =>
        arr.filter(data => !data.disabled).map(data => data.id)
    ).length;
    return checkedCountryIdList.value.length === enabledCountriesCount;
})

const isIndeterminateCountry = computed(() => {
    return checkedCountryIdList.value.length > 0 && !isAllCountryChecked.value
})

const emits = defineEmits<{
    (e: 'handleChange', value: { data: string[], total: number }): void,
}>()

watchEffect(() => {
    emits('handleChange', {
        data: checkedCountryIdList.value,
        total: total.value,
    })
})

const { floatingStyles } = useFloating(countryModalRef, countrySelectRef, {
    placement: 'bottom-start',
    strategy: 'fixed',
    middleware: [
        flip({
            crossAxis: false,
        }),
        offset(4)
    ],
    whileElementsMounted(reference, floating, update) {
        const cleanup = autoUpdate(reference, floating, update, {
            elementResize: true,
            layoutShift: true,
        })
        return cleanup
    },
    ...props.floatingOptions,
})

function onInputFilterCountry() {
    if (!filterKeyword.value) {
        afterFilterListData.value = _.cloneDeep(allCountries);
        return;
    }
    for (const key in allCountries) {
        _.set(afterFilterListData.value, key, _.get(allCountries, key).filter((data:ISelectData) => {
            return _.includes(data.label.toLowerCase(), filterKeyword.value.toLowerCase())
        }))
    }
}

const haveNoFilterData = computed(() => {
    return !_.flatMap(_.values(afterFilterListData.value)).length
})

onClickOutside(
    countryModalRef,
    () => {
        toggle(false)
    }
)

function removeItem(item: ISelectData) {
    for (const key in checkedCountry.value) {
        _.remove(_.get(checkedCountry.value, key), (l) => l === item.id)
    }
}

function handleCheckAllCountry() {
    if (filterKeyword.value) return
    if (isAllCountryChecked.value) {
        for (const key in allCountries) {
            _.set(checkedCountry.value, key, [])
        }
    } else {
        for (const key in allCountries) {
            // 只选择未被禁用的国家
            const enabledCountries = _.get(allCountries, key)
                .filter((d: ISelectData) => !d.disabled)
                .map((d: ISelectData) => d.id);
            _.set(checkedCountry.value, key, enabledCountries)
        }
    }
}

onMounted(async() => {
    const data = await dbConfigDeferred.promise;
    const hasDefaultChecked = !!checkedCountryIdList.value.length
    for (const key in data.patentCountries) {
        _.set(allCountries, key, _.map(_.get(data.patentCountries, key), (f) => {
            const isDisabled = props.countryEnabled ? !props.countryEnabled(key, f) : false;
            return {
                label: t(`commonCountry.${f}`),
                checked: hasDefaultChecked ? checkedCountryIdList.value.includes(f) : true,
                id: f,
                disabled: isDisabled,
            }
        }))
        onInputFilterCountry();
        if (!hasDefaultChecked) {
            // 只选择未被禁用的国家
            const enabledCountries = _.get(data.patentCountries, key).filter((f: string) => {
                return props.countryEnabled ? props.countryEnabled(key, f) : true;
            });
            _.set(checkedCountry.value, key, enabledCountries)
        }
    }
})

const debounceRequest = _.debounce(onInputFilterCountry, 300);

watch(() => filterKeyword.value, () => {
    debounceRequest();
})

const itemsScopeProps = computed(() => {
    return {
        items: chosenList.value,
        total: total.value,
        removeItem,
        toggle,
        visible: mannedCountryPopover.value,
    }
})

const setValue = (value: { ip5: string[], major: string[], others: string[] }) => {
    for (const key in allCountries) {
        _.set(checkedCountry.value, key, _.get(value, key))
    }
}

defineExpose({
    setValue,
    allCountries,
})
</script>

<style lang="scss">
@import './assets/style/authority-position.scss';
.authority-position {
    background-image: url('./assets/images/authority.png');
    background-repeat: no-repeat;
}
</style>
